import { sourceLabel } from "@/components/agents/agent-layout";
import { Badge } from "@/components/ui/badge";
import { LocalDate } from "@/components/ui/local-date";
import { PulseDot } from "@/components/ui/pulse-dot";
import { api } from "@/lib/api";
import { getSocket } from "@/lib/socket";
import { Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** What an agent holds and does now: GET /api/agents/:slug/work. */

export type QueueTicket = {
  workItemId: string;
  globalId: number;
  title: string;
  projectName: string;
  statusName: string | null;
  priority: "low" | "normal" | "high" | "urgent";
  fallback: boolean;
  planPending: boolean;
  criteriaPending: boolean;
  running: boolean;
  since: string;
  works: boolean;
  parked: boolean;
  recentRun: boolean;
  backoff: boolean;
  waitUntil: string | null;
  /** A fallback project's ticket while regular work can be taken now. */
  yields: boolean;
  stall: "note" | "escalate" | "handover" | null;
};

/** One lane at work: a ticket it is present on, or a run without one. */
export type RunNow = {
  id: string;
  source: string | null;
  startedAt: string;
  workItem: { id: string; globalId: number; title: string; projectName: string } | null;
  detail: string | null;
  phase: string | null;
  lastLine: { text: string; at: string } | null;
};

export type LastRun = {
  id: string;
  source: string;
  status: "running" | "completed" | "failed" | "interrupted";
  startedAt: string;
  finishedAt: string | null;
  outcome: string | null;
  error: string | null;
  workItem: { globalId: number; title: string } | null;
};

export type AgentHold = {
  paused: boolean;
  draining: boolean;
  limitedUntil: string | null;
  accessError: string | null;
};

export type AgentWork = {
  tickets: QueueTicket[];
  running: RunNow[];
  alive: Array<{ instance: string; at: string }> | null;
  lastRun: LastRun | null;
  hold: AgentHold;
};

/** Agents with a row in wiki/Cile-a-pravidla.md section 3. */
const PURPOSE_SLUGS = new Set([
  "reviewer",
  "metodej",
  "vratislav",
  "tester",
  "log-triage",
  "wiki-keeper",
  "analyst",
]);

export function AgentPurpose({ slug, full = false }: { slug: string; full?: boolean }) {
  const { t } = useTranslation();
  if (!PURPOSE_SLUGS.has(slug)) return null;
  const k = `agentWork.purpose.${slug}`;
  return (
    <div className="mt-2 max-w-3xl space-y-0.5 text-sm" data-testid="agent-purpose">
      <p>
        <span className="font-medium">{t("agentWork.purpose.label")}:</span> {t(`${k}.text`)}
      </p>
      {full && (
        <p className="text-muted-foreground">
          <span className="font-medium">{t("agentWork.purpose.owns")}:</span> {t(`${k}.owns`)}
        </p>
      )}
      <p className="text-muted-foreground" data-testid="agent-measures">
        <span className="font-medium">{t("agentWork.purpose.measures")}:</span> {t(`${k}.measures`)}
      </p>
    </div>
  );
}

export function waitAge(t: TFunction, since: string, now = Date.now()): string {
  const minutes = Math.max(0, Math.floor((now - new Date(since).getTime()) / 60_000));
  if (minutes < 60) return t("agentWork.age.minutes", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return t("agentWork.age.hours", { count: hours });
  return t("agentWork.age.days", { count: Math.floor(hours / 24) });
}

/** Needs a person's eye: a stall, or a state the agent never acts in. */
export const isFlagged = (q: QueueTicket) => Boolean(q.stall) || (!q.works && !q.parked);

/** Reloads on a work item change and every half minute: runs end without an event. */
export function useAgentWork(slug: string) {
  const [work, setWork] = useState<AgentWork | null>(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(
    () =>
      api
        .get<AgentWork>(`/api/agents/${encodeURIComponent(slug)}/work`)
        .then((w) => {
          setWork(w);
          setFailed(false);
        })
        .catch(() => setFailed(true)),
    [slug],
  );
  useEffect(() => {
    void load();
    const socket = getSocket();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onChange = (payload: { events?: Array<{ model?: string }> }) => {
      if (!payload?.events?.some((e) => e.model === "workItem")) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void load(), 500);
    };
    socket.on("change", onChange);
    const interval = setInterval(() => void load(), 30_000);
    return () => {
      if (timer) clearTimeout(timer);
      clearInterval(interval);
      socket.off("change", onChange);
    };
  }, [load]);
  return { work, failed };
}

export function HoldLines({ hold }: { hold: AgentHold }) {
  const { t } = useTranslation();
  return (
    <>
      {hold.paused && <p data-testid="agent-hold-paused">{t("agentWork.hold.paused")}</p>}
      {hold.draining && <p data-testid="agent-hold-draining">{t("agentWork.hold.draining")}</p>}
      {hold.limitedUntil && (
        <p data-testid="agent-hold-limited">
          {t("agentWork.hold.limited")} <LocalDate value={hold.limitedUntil} withTime />
        </p>
      )}
      {hold.accessError && (
        <p className="text-destructive" data-testid="agent-hold-access">
          {t("agentWork.hold.accessError", { error: hold.accessError })}
        </p>
      )}
    </>
  );
}

/** A lane: what it works on, for how long and the step it is at. */
export function RunNowLine({ run }: { run: RunNow }) {
  const { t } = useTranslation();
  const source = run.source ? sourceLabel(t, run.source) : null;
  const meta = [run.workItem?.projectName, run.workItem ? source : null].filter(Boolean);
  return (
    <div className="flex gap-2.5 text-sm" data-testid="agent-run-now">
      <PulseDot className="mt-1.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          {run.workItem ? (
            <Link
              to="/work-item/$id"
              params={{ id: String(run.workItem.globalId) }}
              className="min-w-0 flex-1 truncate hover:underline"
            >
              <span className="font-medium">#{run.workItem.globalId}</span> {run.workItem.title}
            </Link>
          ) : (
            <span className="min-w-0 flex-1 truncate font-medium">{source}</span>
          )}
          <span
            className="shrink-0 font-medium text-xs tabular-nums"
            data-testid="agent-run-now-age"
          >
            {waitAge(t, run.startedAt)}
          </span>
        </div>
        <span className="block text-muted-foreground text-xs">
          {meta.length ? `${meta.join(" · ")} · ` : ""}
          {t("agentWork.now.since")} <LocalDate value={run.startedAt} withTime />
          {run.phase ? ` · ${t("agentWork.now.phase")}: ${run.phase}` : ""}
        </span>
        {run.detail && (
          <span className="block text-xs" data-testid="agent-run-now-doing">
            <span className="font-medium">{t("agentWork.now.doing")}:</span> {run.detail}
          </span>
        )}
        {run.lastLine && (
          <span className="block truncate font-mono text-muted-foreground text-xs">
            {run.lastLine.text}
          </span>
        )}
      </div>
    </div>
  );
}

/** A coder persona between runs still polls; without this it looks dead. */
export function AliveLine({ alive }: { alive: AgentWork["alive"] }) {
  const { t } = useTranslation();
  if (!alive) return null;
  if (!alive.length) {
    return (
      <p className="text-destructive text-sm" data-testid="agent-gone">
        {t("agentWork.now.gone")}
      </p>
    );
  }
  const last = alive.reduce((a, b) => (a.at > b.at ? a : b));
  return (
    <p className="flex items-center gap-2 text-sm" data-testid="agent-alive">
      <PulseDot />
      <span>
        {t("agentWork.now.alive", { instances: alive.map((s) => s.instance).join(", ") })}{" "}
        <LocalDate value={last.at} withTime />
      </span>
    </p>
  );
}

export function LastRunLine({ run }: { run: LastRun | null }) {
  const { t } = useTranslation();
  if (!run) return <p className="text-muted-foreground">{t("agentWork.now.noRuns")}</p>;
  return (
    <p className="text-muted-foreground text-xs" data-testid="agent-last-run">
      {t("agentWork.now.lastRun")}: {sourceLabel(t, run.source)}
      {run.workItem ? ` #${run.workItem.globalId} ${run.workItem.title}` : ""} ·{" "}
      <span data-testid="agent-last-run-status">{t(`agents.runStatus.${run.status}`)}</span>
      {run.outcome ? ` (${run.outcome})` : ""} ·{" "}
      <LocalDate value={run.finishedAt ?? run.startedAt} withTime />
      {run.error ? ` · ${run.error}` : ""}
    </p>
  );
}

function TicketFlags({ q }: { q: QueueTicket }) {
  const { t } = useTranslation();
  const f = (key: string, variant: "default" | "secondary" | "warning" | "destructive") => (
    <Badge key={key} variant={variant} data-testid={`queue-flag-${key}`}>
      {t(`agentWork.queue.flags.${key}`)}
    </Badge>
  );
  return (
    <>
      {q.running && f("running", "default")}
      {q.parked && f("parked", "secondary")}
      {!q.works && !q.parked && f("wrongState", "warning")}
      {q.stall && f(q.stall, q.stall === "note" ? "warning" : "destructive")}
      {q.planPending && f("plan", "secondary")}
      {q.fallback && f(q.yields ? "fallback" : "fallbackFree", "secondary")}
      {q.recentRun && f("recent", "secondary")}
      {q.backoff && f("backoff", "warning")}
      {q.criteriaPending && !q.running && f("criteria", "secondary")}
    </>
  );
}

export function QueueList({ tickets }: { tickets: QueueTicket[] }) {
  const { t } = useTranslation();
  if (!tickets.length) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="agent-queue-empty">
        {t("agentWork.queue.empty")}
      </p>
    );
  }
  return (
    <ol className="divide-y" data-testid="agent-queue">
      {tickets.map((q, i) => (
        <li key={q.workItemId} className="flex gap-3 py-2" data-testid="agent-queue-row">
          <span className="w-6 shrink-0 text-right text-muted-foreground text-sm">{i + 1}.</span>
          <div className="min-w-0 flex-1">
            <Link
              to="/work-item/$id"
              params={{ id: String(q.globalId) }}
              className="text-sm hover:underline"
            >
              <span className="font-medium">#{q.globalId}</span> {q.title}
            </Link>
            <span className="block text-muted-foreground text-xs">
              {q.projectName}
              {q.statusName ? ` · ${q.statusName}` : ""} ·{" "}
              {t(`workItemList.priority.${q.priority}`)} ·{" "}
              {t("agentWork.queue.waits", { age: waitAge(t, q.since) })}
            </span>
            <div className="mt-1 flex flex-wrap gap-1">
              <TicketFlags q={q} />
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** What the agent does now, lane by lane, and every open ticket it holds, in its order. */
export function AgentNowAndQueue({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const { work, failed } = useAgentWork(slug);
  if (failed && !work) {
    return <p className="text-destructive text-sm">{t("agentWork.queue.loadFailed")}</p>;
  }
  if (!work) {
    return null;
  }
  const flagged = work.tickets.filter(isFlagged).length;
  return (
    <div className="space-y-4">
      <section className="space-y-3 rounded-lg border bg-card p-5" data-testid="agent-now">
        <h2 className="font-bold font-heading text-xl">
          {t("agentWork.now.title")}{" "}
          {work.running.length > 0 && (
            <span className="font-normal text-base text-muted-foreground">
              {t("agentWork.now.lanes", { count: work.running.length })}
            </span>
          )}
        </h2>
        <div className="space-y-1 text-sm">
          <HoldLines hold={work.hold} />
          <AliveLine alive={work.alive} />
        </div>
        {work.running.length ? (
          <div className="divide-y [&>*]:py-2 [&>*:first-child]:pt-0">
            {work.running.map((r) => (
              <RunNowLine key={r.id} run={r} />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{t("agentWork.now.nothing")}</p>
        )}
        <LastRunLine run={work.lastRun} />
      </section>

      <section className="rounded-lg border bg-card p-5">
        <h2 className="font-bold font-heading text-xl">
          {t("agentWork.queue.title")}{" "}
          <span className="font-normal text-base text-muted-foreground" data-testid="queue-count">
            {t("agentWork.queue.count", { count: work.tickets.length })}
            {flagged > 0 && ` · ${t("agentWork.queue.flagged", { count: flagged })}`}
          </span>
        </h2>
        <p className="mt-1 mb-3 text-muted-foreground text-sm">{t("agentWork.queue.intro")}</p>
        <QueueList tickets={work.tickets} />
      </section>
    </div>
  );
}
